import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMe } from '../../api/useMe';
import { bootstrapCsrf, getLogoutUrl } from '../../api/client';
import { useDeleteInvoice } from '../../api/useDeleteInvoice';
import { exportInvoices } from '../../api/useExportInvoices';

export default function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const deleteInvoice = useDeleteInvoice();

  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isInvoicesRoute = location.pathname.startsWith('/invoices');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState<boolean>(Boolean(isSettingsRoute || true));
  const [invoicesDropdownOpen, setInvoicesDropdownOpen] = useState<boolean>(Boolean(isInvoicesRoute || true));
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('color-theme');
      if (saved) return saved === 'dark';
      if (window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
    }
    return false;
  });

  const { data: me } = useMe();

  // Bootstrap CSRF token at app boot
  useEffect(() => {
    bootstrapCsrf();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      if (typeof localStorage !== 'undefined') localStorage.setItem('color-theme', 'dark');
    } else {
      root.classList.remove('dark');
      if (typeof localStorage !== 'undefined') localStorage.setItem('color-theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
  };

  // Derive parses left from API me data
  const parsedCount = me?.parse_count ?? me?.usage?.parsed_count ?? 0;
  const quotaLimit = me?.usage?.quota_limit;
  const explicitRemaining =
    me?.usage?.remaining_quota ??
    me?.usage?.parses_left ??
    me?.usage?.parses_remaining ??
    me?.usage?.remaining;

  let parsesLeftDisplay: string;
  if (explicitRemaining !== undefined && explicitRemaining !== null) {
    parsesLeftDisplay = `${explicitRemaining}`;
  } else if (quotaLimit !== undefined && quotaLimit !== null) {
    parsesLeftDisplay = `${Math.max(0, quotaLimit - parsedCount)}`;
  } else {
    // If unmetered / enterprise plan (e.g. Autworld Plan)
    parsesLeftDisplay = 'Unlimited';
  }

  const planName = me?.subscription?.plan_name ?? 'Free';

  return (
    <div className="flex h-screen overflow-hidden bg-whiten dark:bg-boxdark-2">
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-white duration-300 ease-linear dark:bg-boxdark border-r border-gray-200 dark:border-strokedark lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6 border-b border-brand-line dark:border-strokedark">
          <NavLink to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center flex-shrink-0">
              <img
                src="/logo.svg"
                alt="Invoice Parse Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/logo.png';
                }}
              />
            </div>
            <div>
              <span className="text-lg font-bold text-brand-navy dark:text-white tracking-tight">
                Invoice Parse
              </span>
              <span className="block text-[10px] font-mono font-semibold tracking-wider text-brand-vib uppercase">
                Subscriber App
              </span>
            </div>
          </NavLink>

          <button
            onClick={() => setSidebarOpen(false)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
            className="block lg:hidden text-gray-500 hover:text-gray-700 dark:text-bodydark dark:hover:text-white"
          >
            <svg
              className="w-6 h-6 fill-current"
              viewBox="0 0 24 24"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
              />
            </svg>
          </button>
        </div>

        {/* Sidebar Nav */}
        <div className="flex flex-col overflow-y-auto duration-300 ease-linear p-4">
          <nav className="space-y-6">
            <div>
              <h3 className="mb-3 text-[11px] font-mono font-semibold uppercase text-brand-soft dark:text-bodydark2 tracking-wider px-3">
                Main Menu
              </h3>
              <ul className="space-y-1">
                <li>
                  <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium duration-200 ease-in-out ${
                        isActive
                          ? 'bg-brand-blue-tint text-brand-vib font-semibold dark:bg-brand-900/40 dark:text-brand-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-bodydark dark:hover:bg-strokedark dark:hover:text-white'
                      }`
                    }
                  >
                    <svg
                      className="w-5 h-5 fill-current"
                      viewBox="0 0 24 24"
                    >
                      <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
                    </svg>
                    Dashboard
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/analytics"
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium duration-200 ease-in-out ${
                        isActive
                          ? 'bg-brand-blue-tint text-brand-vib font-semibold dark:bg-brand-900/40 dark:text-brand-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-bodydark dark:hover:bg-strokedark dark:hover:text-white'
                      }`
                    }
                  >
                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
                    </svg>
                    Analytics 360
                  </NavLink>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => setInvoicesDropdownOpen(!invoicesDropdownOpen)}
                    className={`group relative flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-medium duration-200 ease-in-out ${
                      isInvoicesRoute
                        ? 'bg-brand-blue-tint text-brand-vib font-semibold dark:bg-brand-900/40 dark:text-brand-300'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-bodydark dark:hover:bg-strokedark dark:hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-5 h-5 fill-current"
                        viewBox="0 0 24 24"
                      >
                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                      </svg>
                      <span>Invoices</span>
                    </div>
                    <svg
                      className={`w-4 h-4 fill-current transition-transform duration-200 ${
                        invoicesDropdownOpen ? 'rotate-180' : ''
                      }`}
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {/* Invoices Sub-menu with Actions & Tools */}
                  {invoicesDropdownOpen && (
                    <ul className="mt-1.5 flex flex-col gap-1 pl-8 text-xs font-mono">
                      <li>
                        <NavLink
                          to="/invoices"
                          end
                          className={({ isActive }) =>
                            `group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 transition-colors ${
                              isActive
                                ? 'text-brand-vib font-bold bg-brand-blue-tint/50 dark:text-brand-300 dark:bg-brand-900/20'
                                : 'text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white'
                            }`
                          }
                        >
                          Invoice List
                        </NavLink>
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={() => exportInvoices()}
                          className="w-full flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white transition-colors text-left"
                        >
                          <svg className="w-3.5 h-3.5 fill-current text-brand-vib" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          Export CSV
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={() => alert('Opening send email modal...')}
                          className="w-full flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white transition-colors text-left"
                        >
                          <svg className="w-3.5 h-3.5 fill-current text-brand-light" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                          Send via Email
                        </button>
                      </li>
                      <li>
                        {(() => {
                          const pathParts = location.pathname.split('/');
                          const currentInvoiceId =
                            pathParts[1] === 'invoices' && pathParts[2] && pathParts[2] !== 'edit'
                              ? pathParts[2]
                              : null;
                          const editDataUrl = currentInvoiceId ? `/invoices/${currentInvoiceId}/edit` : '/invoices/edit';
                          const isEditActive = location.pathname.includes('/edit');

                          return (
                            <NavLink
                              to={editDataUrl}
                              className={() =>
                                `w-full flex items-center gap-2.5 rounded-lg px-3 py-1.5 transition-colors ${
                                  isEditActive
                                    ? 'text-brand-vib font-bold bg-brand-blue-tint/50 dark:text-brand-300 dark:bg-brand-900/20'
                                    : 'text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white'
                                }`
                              }
                            >
                              <svg className="w-3.5 h-3.5 fill-current text-brand-vib" viewBox="0 0 20 20">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                              Edit Data
                            </NavLink>
                          );
                        })()}
                      </li>
                      <li>
                        <button
                          type="button"
                          onClick={async () => {
                            const pathParts = location.pathname.split('/');
                            const token = pathParts[2] || 'github-aug2026';
                            if (confirm(`Are you sure you want to delete or archive this invoice?`)) {
                              try {
                                await deleteInvoice.mutateAsync(token);
                                alert('Invoice deleted.');
                                navigate('/invoices');
                              } catch {
                                alert('Invoice archived.');
                                navigate('/invoices');
                              }
                            }
                          }}
                          disabled={deleteInvoice.isPending}
                          className="w-full flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 transition-colors text-left disabled:opacity-50"
                        >
                          <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {deleteInvoice.isPending ? 'Deleting…' : 'Archive / Delete'}
                        </button>
                      </li>
                    </ul>
                  )}
                </li>
                <li>
                  <NavLink
                    to="/upload"
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium duration-200 ease-in-out ${
                        isActive
                          ? 'bg-brand-blue-tint text-brand-vib font-semibold dark:bg-brand-900/40 dark:text-brand-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-bodydark dark:hover:bg-strokedark dark:hover:text-white'
                      }`
                    }
                  >
                    <svg
                      className="w-5 h-5 fill-current"
                      viewBox="0 0 24 24"
                    >
                      <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" />
                    </svg>
                    Upload Invoice
                  </NavLink>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mb-3 text-[11px] font-mono font-semibold uppercase text-brand-soft dark:text-bodydark2 tracking-wider px-3">
                Account & Setup
              </h3>
              <ul className="space-y-1">
                <li>
                  <NavLink
                    to="/mcp"
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium duration-200 ease-in-out ${
                        isActive
                          ? 'bg-brand-blue-tint text-brand-vib font-semibold dark:bg-brand-900/40 dark:text-brand-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-bodydark dark:hover:bg-strokedark dark:hover:text-white'
                      }`
                    }
                  >
                    <svg
                      className="w-5 h-5 fill-current"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2a2 2 0 012 2c0 .74-.4 1.38-1 1.72V7h4a3 3 0 013 3v8a3 3 0 01-3 3H7a3 3 0 01-3-3v-8a3 3 0 013-3h4V5.72A2 2 0 0110 4a2 2 0 012-2z" />
                    </svg>
                    MCP Setup
                  </NavLink>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => setSettingsDropdownOpen(!settingsDropdownOpen)}
                    className={`group relative flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-medium duration-200 ease-in-out ${
                      isSettingsRoute
                        ? 'bg-brand-blue-tint text-brand-vib font-semibold dark:bg-brand-900/40 dark:text-brand-300'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-bodydark dark:hover:bg-strokedark dark:hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-5 h-5 fill-current"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                      </svg>
                      <span>Settings</span>
                    </div>
                    <svg
                      className={`w-4 h-4 fill-current transition-transform duration-200 ${
                        settingsDropdownOpen ? 'rotate-180' : ''
                      }`}
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {/* Settings Sub-menu */}
                  {settingsDropdownOpen && (
                    <ul className="mt-1.5 flex flex-col gap-1 pl-8 text-xs font-mono">
                      <li>
                        <NavLink
                          to="/settings?tab=profile"
                          className={() => {
                            const params = new URLSearchParams(location.search);
                            const currentTab = params.get('tab');
                            const isProfileActive =
                              location.pathname === '/settings' && (!currentTab || currentTab === 'profile');
                            return `group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 transition-colors ${
                              isProfileActive
                                ? 'text-brand-vib font-bold bg-brand-blue-tint/50 dark:text-brand-300 dark:bg-brand-900/20'
                                : 'text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white'
                            }`;
                          }}
                        >
                          Profile & Details
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/settings?tab=team"
                          className={() => {
                            const params = new URLSearchParams(location.search);
                            const isTeamActive =
                              location.pathname === '/settings' && params.get('tab') === 'team';
                            return `group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 transition-colors ${
                              isTeamActive
                                ? 'text-brand-vib font-bold bg-brand-blue-tint/50 dark:text-brand-300 dark:bg-brand-900/20'
                                : 'text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white'
                            }`;
                          }}
                        >
                          Team & Members
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/settings?tab=groups"
                          className={() => {
                            const params = new URLSearchParams(location.search);
                            const isGroupsActive =
                              location.pathname === '/settings' && params.get('tab') === 'groups';
                            return `group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 transition-colors ${
                              isGroupsActive
                                ? 'text-brand-vib font-bold bg-brand-blue-tint/50 dark:text-brand-300 dark:bg-brand-900/20'
                                : 'text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white'
                            }`;
                          }}
                        >
                          Sharing Groups
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/settings?tab=developer"
                          className={() => {
                            const params = new URLSearchParams(location.search);
                            const isDevActive =
                              location.pathname === '/settings' && params.get('tab') === 'developer';
                            return `group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 transition-colors ${
                              isDevActive
                                ? 'text-brand-vib font-bold bg-brand-blue-tint/50 dark:text-brand-300 dark:bg-brand-900/20'
                                : 'text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white'
                            }`;
                          }}
                        >
                          APIs & MCP
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/settings?tab=billing"
                          className={() => {
                            const params = new URLSearchParams(location.search);
                            const isBillingActive =
                              location.pathname === '/settings' && params.get('tab') === 'billing';
                            return `group relative flex items-center gap-2.5 rounded-lg px-3 py-1.5 transition-colors ${
                              isBillingActive
                                ? 'text-brand-vib font-bold bg-brand-blue-tint/50 dark:text-brand-300 dark:bg-brand-900/20'
                                : 'text-gray-600 hover:text-brand-navy dark:text-bodydark dark:hover:text-white'
                            }`;
                          }}
                        >
                          Billing & Plan
                        </NavLink>
                      </li>
                    </ul>
                  )}
                </li>
                <li>
                  <a
                    href="https://www.invoice-parse.com/user_home/"
                    className="group relative flex items-center justify-between rounded-xl px-3.5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-bodydark dark:hover:bg-strokedark dark:hover:text-white duration-200 ease-in-out"
                    title="Navigate back to the classic User Home page"
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-5 h-5 fill-current text-gray-500 group-hover:text-brand-vib dark:text-bodydark dark:group-hover:text-white transition-colors"
                        viewBox="0 0 24 24"
                      >
                        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                      </svg>
                      <span>Old Home</span>
                    </div>
                    <span className="text-xs text-brand-soft group-hover:text-brand-navy dark:group-hover:text-white font-mono">
                      ↗
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </div>

        {/* Sidebar Footer Organization Info */}
        {me?.organization && (
          <div className="mt-auto p-4 border-t border-brand-line dark:border-strokedark">
            <div className="bg-brand-bg dark:bg-strokedark/50 rounded-xl p-3 border border-brand-line dark:border-strokedark">
              <div className="text-[10px] font-mono font-semibold text-brand-soft dark:text-bodydark2 uppercase tracking-wider">
                Organization
              </div>
              <div className="text-sm font-semibold text-brand-navy dark:text-white truncate mt-0.5">
                {me.organization.name}
              </div>
              {me.organization.public_token && (
                <div className="text-[11px] font-mono text-brand-soft dark:text-bodydark2 truncate mt-0.5">
                  {me.organization.public_token}
                </div>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* Content Area */}
      <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
        {/* Header */}
        <header className="sticky top-0 z-30 flex w-full bg-white dark:bg-boxdark border-b border-brand-line dark:border-strokedark shadow-card">
          <div className="flex flex-grow items-center justify-between py-3.5 px-4 md:px-6 2xl:px-11">
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Hamburger Button */}
              <button
                aria-controls="sidebar"
                onClick={(e) => {
                  e.stopPropagation();
                  setSidebarOpen(!sidebarOpen);
                }}
                className="z-30 block rounded-lg border border-gray-200 bg-white p-1.5 shadow-sm hover:bg-gray-50 dark:border-strokedark dark:bg-boxdark lg:hidden"
              >
                <svg
                  className="w-5 h-5 fill-current text-gray-600 dark:text-bodydark"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z"
                  />
                </svg>
              </button>

              <div className="hidden sm:flex items-center gap-2">
                <span className="text-[11px] font-mono font-semibold uppercase tracking-wider text-brand-vib bg-brand-blue-tint dark:bg-brand-950/60 dark:text-brand-300 px-3 py-1 rounded-full border border-brand-vib/15 dark:border-brand-800">
                  {planName} Plan
                </span>
                <NavLink
                  to="/mcp"
                  className={({ isActive }) =>
                    `px-3 py-1 rounded-full text-xs font-mono font-semibold transition-colors border ${
                      isActive
                        ? 'bg-brand-vib text-white border-brand-vib'
                        : 'bg-brand-bg dark:bg-strokedark/60 text-brand-navy dark:text-white border-brand-line dark:border-strokedark hover:bg-brand-blue-tint hover:text-brand-vib'
                    }`
                  }
                >
                  MCP Setup
                </NavLink>
              </div>
            </div>

            {/* Right side items: Lightning Parse Count, Dark Mode, User Email & Logout */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Parse Count Indicator (Lightning pill matching upload.html) */}
              <div
                className="flex items-center gap-1.5 font-mono text-[11px] font-medium text-brand-muted dark:text-bodydark bg-brand-blue-tint border border-brand-vib/15 dark:bg-strokedark/60 dark:border-strokedark px-3.5 py-1.5 rounded-full"
                title={
                  quotaLimit
                    ? `${parsedCount} used of ${quotaLimit} monthly parses`
                    : `Unlimited parses available on your ${planName}`
                }
              >
                <svg
                  className="w-3.5 h-3.5 text-brand-vib fill-current"
                  viewBox="0 0 24 24"
                >
                  <path d="M7 2v11h3v9l7-12h-4l4-8z" />
                </svg>
                <span className="font-semibold text-brand-navy dark:text-white">
                  {parsesLeftDisplay}
                </span>
                {quotaLimit !== undefined && quotaLimit !== null && (
                  <span className="text-brand-soft dark:text-bodydark2">
                    /{quotaLimit}
                  </span>
                )}
                <span>left</span>
              </div>

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                aria-label="Toggle Dark Mode"
                className="relative flex h-8.5 w-8.5 items-center justify-center rounded-full border border-brand-line bg-brand-bg text-gray-600 hover:bg-gray-100 dark:border-strokedark dark:bg-meta-4 dark:text-bodydark dark:hover:text-white"
              >
                {darkMode ? (
                  // Sun Icon
                  <svg
                    className="w-4 h-4 fill-current text-yellow-400"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  // Moon Icon
                  <svg
                    className="w-4 h-4 fill-current text-gray-700"
                    viewBox="0 0 20 20"
                  >
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>

              {/* User Profile & Email */}
              <div className="flex items-center gap-3 border-l border-gray-200 dark:border-strokedark pl-3 sm:pl-5">
                <div className="flex flex-col text-right">
                  <span className="text-xs font-semibold text-gray-900 dark:text-white leading-tight">
                    {me ? (
                      me.first_name || me.last_name ? (
                        `${me.first_name} ${me.last_name}`.trim()
                      ) : (
                        me.email
                      )
                    ) : (
                      'Loading...'
                    )}
                  </span>
                  {me && (me.first_name || me.last_name) && (
                    <span className="text-[11px] text-gray-400 dark:text-bodydark2 truncate max-w-[150px]">
                      {me.email}
                    </span>
                  )}
                </div>

                {/* Logout Link */}
                <a
                  href={getLogoutUrl()}
                  title="Sign out"
                  className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40 duration-150"
                >
                  <svg
                    className="w-4 h-4 fill-current"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                  </svg>
                  <span className="hidden md:inline">Sign out</span>
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Body */}
        <main className="w-full flex-1 p-4 md:p-6 2xl:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
